import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";

function createFallbackTexture() {
  const data = new Uint8Array([
    15, 28, 39, 255,
    35, 56, 79, 255,
    35, 56, 79, 255,
    15, 28, 39, 255
  ]);

  const texture = new THREE.DataTexture(data, 2, 2, THREE.RGBAFormat);
  texture.needsUpdate = true;
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

export class Resources {
  constructor(manifest, { isMobile = false } = {}) {
    this.manifest = manifest;
    this.isMobile = isMobile;
    this.items = new Map();
    this.events = new EventTarget();
    this.fallbackTexture = createFallbackTexture();
  }

  on(eventName, listener) {
    this.events.addEventListener(eventName, listener);
  }

  emit(eventName, detail) {
    this.events.dispatchEvent(new CustomEvent(eventName, { detail }));
  }

  async load() {
    const preloadEntries = this.manifest.filter((item) => item.preload);
    const total = preloadEntries.length;

    if (total === 0) {
      this.emit("ready", { total: 0 });
      return { total: 0, errors: 0 };
    }

    let loaded = 0;
    let errors = 0;

    await Promise.all(
      preloadEntries.map(async (entry) => {
        try {
          const item = await this.loadEntry(entry);
          this.items.set(entry.name, item);
        } catch (_error) {
          errors += 1;
          this.items.set(entry.name, this.getFallbackForType(entry.type));
        } finally {
          loaded += 1;
          this.emit("progress", {
            loaded,
            total,
            value: loaded / total,
            errors
          });
        }
      })
    );

    this.emit("ready", { total, errors });
    return { total, errors };
  }

  loadEntry(entry) {
    if (entry.type === "texture") {
      return this.loadTexture(entry);
    }

    if (entry.type === "hdr") {
      return this.loadHDR(entry);
    }

    if (entry.type === "gltf") {
      return this.loadGLTF(entry);
    }

    return Promise.resolve(this.getFallbackForType(entry.type));
  }

  getFallbackForType(type) {
    if (type === "gltf" || type === "hdr") {
      return null;
    }

    return this.fallbackTexture;
  }

  loadTexture(entry) {
    const loader = new THREE.TextureLoader();
    const path = this.isMobile && entry.mobileVariant ? entry.mobileVariant : entry.path;

    return new Promise((resolve, reject) => {
      loader.load(
        path,
        (texture) => {
          texture.colorSpace = THREE.SRGBColorSpace;
          texture.minFilter = THREE.LinearMipmapLinearFilter;
          texture.magFilter = THREE.LinearFilter;
          texture.anisotropy = 4;
          resolve(texture);
        },
        undefined,
        reject
      );
    });
  }

  loadHDR(entry) {
    const loader = new RGBELoader();

    return new Promise((resolve, reject) => {
      loader.load(
        entry.path,
        (texture) => {
          texture.mapping = THREE.EquirectangularReflectionMapping;
          resolve(texture);
        },
        undefined,
        reject
      );
    });
  }

  loadGLTF(entry) {
    const loader = new GLTFLoader();

    return new Promise((resolve, reject) => {
      loader.load(
        entry.path,
        (gltf) => resolve(gltf),
        undefined,
        reject
      );
    });
  }

  get(name, fallback = this.fallbackTexture) {
    return this.items.get(name) ?? fallback;
  }

  dispose() {
    for (const item of this.items.values()) {
      if (item && typeof item.dispose === "function") {
        item.dispose();
      }
    }

    this.items.clear();
    this.fallbackTexture.dispose();
  }
}
